import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { FileNotificationsStore } from "files/notifications/filter-role/file-notifications-store.logic";
import { ActivatedRoute, Router } from "@angular/router";
import { PageableDataSource } from "common/data/pageable-data-source.logic";
import { MutexService } from "common/concurrency/mutex.service";
import { combineLatest, Subject } from "rxjs";
import { FilesNotificationsService } from "files/notifications/filter-role/files-notifications.service";
import { SelectionOption } from "common/data/selection-filter.component";
import { Mode } from "common/data/data-view.component";
import { skip, takeUntil } from "rxjs/operators";
import { SelectionRepository } from "common/selection/state/selection.repository";
import TypedId = stages.core.TypedId;

type UserWithAssignedRoles = stages.process.elements.UserWithAssignedRoles;

export interface NotificationInformation {
	notifyUsers: boolean;
	selectedUserIds: string[];
	notSelectedUserIds: string[];
	selectAllActive: boolean;
	deselectAllActive: boolean;
}

@Component({
	selector: "stages-files-notifications",
	templateUrl: "./files-notifications.component.html",
	styleUrls: ["files-notifications.component.scss"],
})
export class FilesNotificationsComponent implements OnInit, OnDestroy {
	filterMap: Map<string, SelectionOption[]> = new Map<string, SelectionOption[]>();
	notificationForm!: UntypedFormGroup;
	dataSource!: PageableDataSource<UserWithAssignedRoles>;
	FilesNotificationsComponent = FilesNotificationsComponent;
	private destroy$ = new Subject<boolean>();
	fileNotificationsStore?: FileNotificationsStore;
	pageSize: number = 10;
	mode: Mode = Mode.SELECT;
	storeIdentifierAssociatedUsers: string = "filesNotificationsAssociatedUsers";
	typeIdent = "user";

	@Output()
	readonly notificationInformation = new EventEmitter<NotificationInformation>();

	@Input()
	isNewFile?: boolean;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private filesNotificationService: FilesNotificationsService,
		private mutexService: MutexService,
		private selectionRepository: SelectionRepository,
	) {}

	ngOnInit(): void {
		this.createNotificationForm();
		this.fileNotificationsStore = new FileNotificationsStore(
			this.pageSize,
			this.filesNotificationService,
			this.isNewFile,
		);
		this.dataSource = new PageableDataSource(this.fileNotificationsStore, this.mutexService, this.router, this.route);

		combineLatest([
			this.selectionRepository.getAllSelectedCommon$(this.storeIdentifierAssociatedUsers),
			this.selectionRepository.getAllNotSelectedFromCommon$(this.storeIdentifierAssociatedUsers),
			this.selectionRepository.isSelectAllPressedCommon$,
			this.selectionRepository.isDeselectAllPressedCommon$,
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([selected, notSelected, isSelectAllPressed, isDeselectAllPressed]) => {
				const selectedUserIds: string[] = [];
				const notSelectedUserIds: string[] = [];
				if (selected) {
					selected.forEach((user) => {
						selectedUserIds.push(user.id);
					});
				}
				if (notSelected) {
					notSelected.forEach((user) => {
						notSelectedUserIds.push(user.id);
					});
				}
				const updatedNotificationInformation = {
					notifyUsers: true,
					selectedUserIds: selectedUserIds,
					notSelectedUserIds: notSelectedUserIds,
					selectAllActive: isSelectAllPressed,
					deselectAllActive: isDeselectAllPressed,
				};
				this.notificationInformation.emit(updatedNotificationInformation);
			});

		this.filesNotificationService.nextLifecycleState$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
			this.selectionRepository.resetCommon();
			this.filterMap.clear();
			this.dataSource.reloadPage();
			this.selectionRepository.updateInitiallySelectedCommon(this.storeIdentifierAssociatedUsers);
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
		this.selectionRepository.resetCommon();
		this.notificationInformation.emit({
			notifyUsers: false,
			selectedUserIds: [],
			notSelectedUserIds: [],
			selectAllActive: false,
			deselectAllActive: false,
		});
	}

	private createNotificationForm(): void {
		this.notificationForm = new UntypedFormGroup({
			message: new UntypedFormControl(null),
		});
	}

	static getUserId(userRole: UserWithAssignedRoles): string {
		return userRole.id;
	}

	static getUserTypedId(userRole: UserWithAssignedRoles): TypedId {
		return { id: userRole.id, typeIdent: "user" };
	}

	static isDisabled(userRoles: UserWithAssignedRoles): boolean {
		return userRoles.emailAddress === null;
	}

	static isSelectable(userRoles: UserWithAssignedRoles): boolean {
		return userRoles.emailAddress === null;
	}

	static isPreselected(userRoles: UserWithAssignedRoles): boolean {
		return userRoles.preselected && userRoles.emailAddress !== null;
	}
}
